import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileCopy as CloneIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import type { Schema } from '../../services/api';
import GuidDisplay from '../common/GuidDisplay';
import { formatTimestamp } from '../../utils/dataUtils';
import { statusOptions, statusLabels } from './types';

interface SchemaSetTableProps {
  schemaSets: Schema[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onCommitSearch: () => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onOpenCreate: () => void;
  onViewDetails: (schema: Schema) => void;
  onClone: (schema: Schema) => void;
  onDelete: (schema: Schema) => void;
}

const SchemaSetTable: React.FC<SchemaSetTableProps> = ({
  schemaSets,
  isLoading,
  isError,
  error,
  searchInput,
  onSearchInputChange,
  onCommitSearch,
  statusFilter,
  onStatusFilterChange,
  onOpenCreate,
  onViewDetails,
  onClone,
  onDelete,
}) => (
  <>
    <Box display="flex" justifyContent="space-between" mb={2}>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenCreate}>
        New Schema
      </Button>
    </Box>

    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Search by name"
            value={searchInput}
            onChange={(event) => onSearchInputChange(event.target.value)}
            onBlur={onCommitSearch}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onCommitSearch();
              }
            }}
            helperText="Minimum 3 characters"
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Status"
            value={statusFilter}
            onChange={(event) => onStatusFilterChange(event.target.value)}
            select
            fullWidth
            size="small"
          >
            <MenuItem value="">
              <em>All statuses</em>
            </MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {statusLabels[status] ?? status}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Paper>

    {isLoading ? (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    ) : isError ? (
      <Alert severity="error">
        Unable to load schemas.{' '}
        {error instanceof Error ? error.message : 'Please try again later.'}
      </Alert>
    ) : schemaSets.length === 0 ? (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No schemas found for the current filters.
        </Typography>
      </Paper>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schemaSets.map((schema) => (
              <TableRow hover key={schema.schema_id}>
                <TableCell>
                  <Stack spacing={0.3}>
                    <Typography variant="body2" fontWeight={600}>
                      {schema.schema_name}
                    </Typography>
                    <GuidDisplay guid={schema.schema_id} abbreviate />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={statusLabels[schema.status] ?? schema.status} size="small" color={schema.status === 'active' ? 'success' : 'default'} />
                </TableCell>
                <TableCell>{schema.version}</TableCell>
                <TableCell>
                  <Typography variant="body2">{formatTimestamp(schema.updated_at)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="View details">
                      <span>
                        <IconButton size="small" onClick={() => onViewDetails(schema)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Clone schema">
                      <span>
                        <IconButton size="small" onClick={() => onClone(schema)}>
                          <CloneIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete schema permanently">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDelete(schema)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </>
);

export default SchemaSetTable;
